import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { IRatingForm, IRatingFormQuestion } from '../../Interfaces/IRatingForm';
import {
  THRESHOLD_MAX,
  THRESHOLD_MIN,
  nextClientKey,
  mailSectionSummary,
  thanksSectionSummary,
  thresholdOnBlur,
} from './ratingFormUtils';
import { QuestionRow } from './QuestionRow';

export const newRatingQuestion = (): IRatingFormQuestion => ({
  Label: '',
  QuestionType: 'stars',
  RangeMin: 1,
  RangeMax: 5,
  IsRequired: true,
  CountsForOverall: true,
  OrderNo: 0,
});

interface IProps {
  value: IRatingForm;
  onChange: (next: IRatingForm) => void;
}

export const RatingFormStructureEditor: React.FC<IProps> = ({
  value,
  onChange,
}) => {
  // Beim ersten Rendern eines Bogens mit genau einer noch unbenannten Frage
  // (= "Neuer Bewertungsbogen") direkt aufgeklappt starten, sonst sieht man
  // nur eine kursive Zeile und muss erst klicken, um ueberhaupt etwas
  // ausfuellen zu koennen. Als Lazy-Initializer, damit das nur beim Mount
  // greift und bestehende Boegen beim Bearbeiten unangetastet bleiben.
  const [expandedKey, setExpandedKey] = React.useState<string | null>(() => {
    const initialQuestions = value.Questions ?? [];
    if (
      initialQuestions.length === 1 &&
      (initialQuestions[0].Label ?? '').trim() === ''
    ) {
      return initialQuestions[0]._clientKey ?? null;
    }
    return null;
  });
  const sensors = useSensors(
    // Kleiner Schwellwert, damit ein Klick auf die Zeile weiterhin
    // aufklappt und nicht als Ziehen missverstanden wird.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const patch = (p: Partial<IRatingForm>) => onChange({ ...value, ...p });

  const updateQuestion = (idx: number, qp: Partial<IRatingFormQuestion>) => {
    const next = [...(value.Questions ?? [])];
    next[idx] = { ...next[idx], ...qp };
    patch({ Questions: next });
  };

  const moveQuestion = (idx: number, dir: -1 | 1) => {
    const list = [...(value.Questions ?? [])];
    const target = idx + dir;
    if (target < 0 || target >= list.length) return;
    [list[idx], list[target]] = [list[target], list[idx]];
    patch({ Questions: list });
  };

  const removeQuestion = (idx: number) => {
    const list = [...(value.Questions ?? [])];
    list.splice(idx, 1);
    patch({ Questions: list });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const list = value.Questions ?? [];
    const from = list.findIndex(q => q._clientKey === active.id);
    const to = list.findIndex(q => q._clientKey === over.id);
    if (from < 0 || to < 0) return;
    patch({ Questions: arrayMove(list, from, to) });
  };

  const addQuestion = () => {
    const created = { ...newRatingQuestion(), _clientKey: nextClientKey() };
    patch({ Questions: [...(value.Questions ?? []), created] });
    setExpandedKey(created._clientKey ?? null);
  };

  // Gemeinsamer Ausdruck fuer SortableContext-`items`, React-`key` und
  // `sortableId`: alle drei muessen fuer dieselbe Frage exakt gleich sein,
  // sonst laeuft `indexOf` in `handleDragEnd` ins Leere und das Sortieren
  // bleibt wortlos wirkungslos.
  const questionSortableId = (q: IRatingFormQuestion, idx: number): string =>
    q._clientKey ?? String(idx);

  return (
    <Stack spacing={3}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <TextField
            label='Titel'
            fullWidth
            required
            value={value.Title}
            onChange={e => patch({ Title: e.target.value })}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <FormControlLabel
            control={
              <Switch
                checked={!!value.IsActive}
                onChange={(_, v) => patch({ IsActive: v })}
              />
            }
            label='Aktiv'
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <FormControlLabel
            control={
              <Switch
                checked={!!value.IsDefault}
                onChange={(_, v) => patch({ IsDefault: v })}
              />
            }
            label='Standard'
          />
        </Grid>
      </Grid>

      <Divider />

      <Box>
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          sx={{ mb: 1 }}
        >
          <Typography variant='subtitle1'>Fragen</Typography>
          <Button size='small' startIcon={<AddIcon />} onClick={addQuestion}>
            Frage hinzufügen
          </Button>
        </Stack>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={() => setExpandedKey(null)}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={(value.Questions ?? []).map((q, idx) =>
              questionSortableId(q, idx)
            )}
            strategy={verticalListSortingStrategy}
          >
            <Box>
              {(value.Questions ?? []).map((q, idx) => (
                <QuestionRow
                  key={questionSortableId(q, idx)}
                  sortableId={questionSortableId(q, idx)}
                  question={q}
                  expanded={expandedKey === q._clientKey}
                  isFirst={idx === 0}
                  isLast={idx === (value.Questions?.length ?? 0) - 1}
                  onToggle={() =>
                    setExpandedKey(
                      expandedKey === q._clientKey
                        ? null
                        : (q._clientKey ?? null)
                    )
                  }
                  onChange={qp => updateQuestion(idx, qp)}
                  onMove={dir => moveQuestion(idx, dir)}
                  onRemove={() => removeQuestion(idx)}
                />
              ))}
            </Box>
          </SortableContext>
        </DndContext>
      </Box>

      <Box>
        <Accordion disableGutters variant='outlined'>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ flex: 1 }}>E-Mail-Versand</Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mr: 1 }}>
              {mailSectionSummary(value)}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{ display: 'block', mb: 2 }}
            >
              Die Vorlagen für Einladung und Eingangsbestätigung sind fest und
              werden zentral in „Systemnachrichten → Bewertungsbögen" gepflegt.
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={!!value.ConfirmationEnabled}
                  onChange={(_, v) => patch({ ConfirmationEnabled: v })}
                />
              }
              label='Eingangsbestätigung an den Mandanten senden'
            />
          </AccordionDetails>
        </Accordion>

        <Accordion disableGutters variant='outlined'>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ flex: 1 }}>
              Dankeschön / Bewertungs-Anstoß
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mr: 1 }}>
              {thanksSectionSummary(value)}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{ display: 'block', mb: 2 }}
            >
              Der <strong>Dankestext</strong> wird dem Mandanten nach Abgabe der
              Bewertung angezeigt und – sofern oben die Eingangsbestätigung
              aktiviert ist – als Platzhalter{' '}
              <code>{'{{RatingThanksText}}'}</code> in die Bestätigungs-E-Mail
              eingefügt. Der <strong>Bewertungs-Link</strong> (Linktext +
              Link-URL) erscheint dem Mandanten zusätzlich nur dann, wenn der
              Gesamtwert seiner Bewertung den Schwellenwert erreicht oder
              überschreitet.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  label='Schwellenwert (≥)'
                  type='number'
                  fullWidth
                  helperText={`Ab diesem Gesamtwert wird der Link angezeigt (${THRESHOLD_MIN}–${THRESHOLD_MAX}).`}
                  inputProps={{
                    min: THRESHOLD_MIN,
                    max: THRESHOLD_MAX,
                    step: 0.1,
                  }}
                  value={value.ThresholdScore ?? ''}
                  onChange={e =>
                    patch({
                      ThresholdScore:
                        e.target.value === '' ? null : Number(e.target.value),
                    })
                  }
                  // Erst beim Verlassen einrasten, nicht beim Tippen —
                  // gleiches Muster wie bei der Skalen-Obergrenze.
                  onBlur={() =>
                    patch({ ThresholdScore: thresholdOnBlur(value.ThresholdScore) })
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end'>
                        <Tooltip
                          title={
                            <Box sx={{ py: 0.5 }}>
                              <Typography variant='body2' sx={{ mb: 0.75 }}>
                                Der Gesamtwert liegt immer zwischen 1 und 5,
                                unabhängig von den Skalen im Bogen.
                              </Typography>
                              <Typography variant='body2' sx={{ mb: 0.75 }}>
                                Jede Antwort wird dafür auf ihrer eigenen Skala
                                umgerechnet: 4 von 10 ergibt 2,33 — 4 von 5
                                ergibt 4,00.
                              </Typography>
                              <Typography variant='body2'>
                                Alle Fragen mit „In Gesamtwert einrechnen“
                                gehen gleichrangig ein.
                              </Typography>
                            </Box>
                          }
                          // Als fokussierbarer Knopf, nicht als nacktes Icon:
                          // sonst ist der Hinweis per Tastatur und auf
                          // Touch-Geraeten nicht erreichbar.
                          enterTouchDelay={0}
                        >
                          <IconButton
                            size='small'
                            edge='end'
                            aria-label='Wie wird der Gesamtwert berechnet?'
                            sx={{ color: 'text.secondary' }}
                          >
                            <InfoOutlinedIcon fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField
                  label='Linktext'
                  fullWidth
                  value={value.ThanksLinkLabel ?? ''}
                  onChange={e => patch({ ThanksLinkLabel: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label='Link-URL (z. B. Google-Bewertung)'
                  fullWidth
                  placeholder='https://g.page/r/.../review'
                  helperText='Vollständige Adresse inklusive https:// — fehlt das, wird sie beim Speichern ergänzt.'
                  value={value.ThanksLinkUrl ?? ''}
                  onChange={e => patch({ ThanksLinkUrl: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label='Dankestext'
                  fullWidth
                  multiline
                  minRows={3}
                  value={value.ThanksText ?? ''}
                  onChange={e => patch({ ThanksText: e.target.value })}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Box>
    </Stack>
  );
};
